import React, { useEffect, useState, useCallback, useMemo } from "react";
import JumboContentLayout from "@jumbo/components/JumboContentLayout";
import {
  Card,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import { useAPI } from "app/hooks/useApi";
import { useSmartNavigation } from "app/utils/navigationHelper";
import Swal from "sweetalert2";
import ProcessUploadArea from "app/pages/components/ProcessUploadArea";
import { LoadingButton } from "@mui/lab";

const validationSchema = yup.object({
  numero_processo: yup.string().when("arquivoProcesso", {
    is: (arquivoProcesso) => !arquivoProcesso,
    then: yup.string().required("Informe o número do processo ou faça upload do PDF"),
    otherwise: yup.string().optional(),
  }),
  tipo_processo: yup.string().optional(),
  responsavel_id: yup.number().optional(),
  envolvido_id: yup.number().optional(),
});

const CadastroMarcasRPI = () => {
  const { makeRequest, loading } = useAPI();
  const { navigateToProcesso } = useSmartNavigation();

  const initialValues = {
    numero_processo: "",
    tipo_processo: "",
    responsavel_id: "",
    envolvido_id: "",
    arquivoProcesso: null,
  };
  const [responsaveis, setResponsaveis] = useState([]);
  const [envolvidos, setEnvolvidos] = useState([]);
  const [dataLoaded, setDataLoaded] = useState({ responsaveis: false, envolvidos: false });

  const getResponsaveis = useCallback(async () => {
    if (dataLoaded.responsaveis) return;
    
    try {
      const response = await makeRequest(
        "users?role=ADMIN,OPERADOR&page=0&perPage=10"
      );
      if (response.status === 200) {
        setResponsaveis(response.data.users);
        setDataLoaded(prev => ({ ...prev, responsaveis: true }));
      }
    } catch (error) {

      Swal.fire("error", "Erro ao carregar lista de responsáveis", "error");
    }
  }, [makeRequest, dataLoaded.responsaveis]);

  const getEnvolvidos = useCallback(async () => {
    if (dataLoaded.envolvidos) return;
    
    try {
      const response = await makeRequest("envolvidos");
      if (response.status === 200) {
        setEnvolvidos(response.data.envolvidos);
        setDataLoaded(prev => ({ ...prev, envolvidos: true }));
      }
    } catch (error) {

      Swal.fire("error", "Erro ao carregar lista de envolvidos", "error");
    }
  }, [makeRequest, dataLoaded.envolvidos]);

  const memoizedResponsaveis = useMemo(
    () =>
      responsaveis &&
      responsaveis.map((responsavel) => (
        <MenuItem key={responsavel.id} value={responsavel.id}>
          {responsavel.name}
        </MenuItem>
      )),
    [responsaveis]
  );

  const memoizedEnvolvidos = useMemo(
    () =>
      (envolvidos || []).map((envolvido) => (
        <MenuItem key={envolvido.id} value={envolvido.id}>
          {envolvido.nome_identificacao}
        </MenuItem>
      )),
    [envolvidos]
  );

  useEffect(() => {
    getResponsaveis();
    getEnvolvidos();
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    const formData = new FormData();
    formData.append("numero_processo", values.numero_processo);
    formData.append(
      "is_processo_de_terceiro",
      values.tipo_processo === "terceiro"
    );
    formData.append(
      "is_acompanhado_pela_bunker",
      values.tipo_processo === "acompanhamento"
    );
    formData.append("responsavel_id", values.responsavel_id);
    formData.append("envolvido_id", values.envolvido_id);

    if (values.arquivoProcesso) {
      formData.append("arquivoProcesso", values.arquivoProcesso);
    }

    try {
      const response = await makeRequest(
        "/processos-revistas-rpi/watch",
        "POST",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      if (response.status === 201) {
        const { id } = response.data;
        
        if (values.arquivoProcesso) {
          Swal.fire({
            title: "Sucesso!",
            text: "Processo criado automaticamente a partir do PDF",
            icon: "success",
            confirmButtonText: "Ver processo"
          });
        } else {
          Swal.fire("success", "Processo gravado com sucesso", "success");
        }
        
        navigateToProcesso(id);
      }
    } catch (error) {

      let errorMessage = "Erro ao gravar processo";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      Swal.fire("error", errorMessage, "error");
    }
    setSubmitting(false);
  };

  return (
    <JumboContentLayout
      header={<h1 variant="h6">Cadastramento de processos - Marcas</h1>}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validateOnBlur={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          setFieldValue,
          errors,
          handleBlur,
          handleChange,
          setFieldTouched,
          touched,
        }) => (
          <Form width="100%">
            <Card width="100%" elevation={1} sx={{ p: 2 }}>
              <Grid
                container
                width="100%"
                spacing={2}
                display={"flex"}
                alignItems={"center"}
              >
                <Grid item xs={3}>
                  <Typography fontWeight={600} mb={1}>
                    Nº do processo {values.arquivoProcesso && <span style={{color: 'green', fontSize: '12px'}}>(será extraído do PDF)</span>}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="numero_processo"
                    value={values.numero_processo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={values.arquivoProcesso ? "Opcional - será extraído do PDF" : "Informe o número do processo"}
                    disabled={values.arquivoProcesso ? true : false}
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: values.arquivoProcesso ? '#f5f5f5' : 'white'
                      }
                    }}
                  />
                  {touched.numero_processo && errors.numero_processo && (
                    <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                      {errors.numero_processo}
                    </div>
                  )}
                </Grid>

                <Grid
                  item
                  xs={2}
                  display={"flex"}
                  alignItems={"flex-start"}
                  flexDirection={"column"}
                >
                  <Typography fontWeight={600} mb={1}>
                    Tipo de Processo
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    name="tipo_processo"
                    value={values.tipo_processo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="">Selecione</MenuItem>
                    <MenuItem value="terceiro">Terceiro</MenuItem>
                    <MenuItem value="acompanhamento">Acompanhamento</MenuItem>
                  </Select>
                  {touched.tipo_processo && errors.tipo_processo && (
                    <div style={{ color: "red" }}>{errors.tipo_processo}</div>
                  )}
                </Grid>

                <Grid item xs={2}>
                  <Typography fontWeight={600} mb={1}>
                    Responsável
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    name="responsavel_id"
                    value={values.responsavel_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="">Selecione</MenuItem>
                    {memoizedResponsaveis}
                  </Select>
                  {touched.responsavel_id && errors.responsavel_id && (
                    <div style={{ color: "red" }}>{errors.responsavel_id}</div>
                  )}
                </Grid>

                <Grid item xs={3}>
                  <Typography fontWeight={600} mb={1}>
                    Envolvido
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    name="envolvido_id"
                    value={values.envolvido_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="">Selecione</MenuItem>
                    {memoizedEnvolvidos}
                  </Select>
                  {touched.envolvido_id && errors.envolvido_id && (
                    <div style={{ color: "red" }}>{errors.envolvido_id}</div>
                  )}
                </Grid>
              </Grid>

              <Stack
                my={1}
                width={"100%"}
                p={2}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <LoadingButton
                  color="info"
                  type="submit"
                  variant="contained"
                  loading={loading}
                >
                  Gravar
                </LoadingButton>
              </Stack>
              <Divider />
              
              {/* Informação sobre o upload de PDF */}
              <Stack my={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                <Typography variant="h6" fontWeight={600} mb={1} color="primary">
                  📄 Upload de PDF Inteligente
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  • Faça upload de PDFs de protocolo do INPI para extração automática de dados
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  • O sistema identifica automaticamente: número do processo, marca, classe, cliente e mais
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Quando há PDF, o número do processo torna-se opcional (será extraído automaticamente)
                </Typography>
              </Stack>
              
              <ProcessUploadArea
                onFileChange={(file) => {
                  setFieldValue("arquivoProcesso", file);
                }}
              />

              <Stack
                my={1}
                width={"100%"}
                p={2}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <LoadingButton
                  color="info"
                  type="submit"
                  variant="contained"
                  loading={loading}
                >
                  Gravar
                </LoadingButton>
              </Stack>
            </Card>
          </Form>
        )}
      </Formik>
    </JumboContentLayout>
  );
};

export default CadastroMarcasRPI;
